/**
 * Name validation utilities for signup forms
 * Ensures quality data entry for first and last names
 */

// Regex patterns
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NUMBERS_ONLY_PATTERN = /^[\d\s]+$/;
const SPECIAL_CHARS_ONLY_PATTERN = /^[^a-zA-Z]+$/;
const CONTAINS_URL_PATTERN = /https?:\/\/|www\./i;

/**
 * Validate a name field
 * @param {string} name - The name to validate
 * @param {string} fieldLabel - "First name" or "Last name" for error messages
 * @returns {{ valid: boolean, error: string | null }}
 */
export const validateName = (name, fieldLabel = 'Name') => {
  const trimmed = name?.trim() || '';
  
  // Check if empty
  if (!trimmed) {
    return { valid: false, error: `${fieldLabel} is required` };
  }
  
  // Check minimum length (3 characters minimum)
  if (trimmed.length < 3) {
    return { valid: false, error: `${fieldLabel} must be at least 3 characters` };
  }
  
  // Check maximum length
  if (trimmed.length > 50) {
    return { valid: false, error: `${fieldLabel} is too long (max 50 characters)` };
  }
  
  // Check if it's an email address
  if (EMAIL_PATTERN.test(trimmed)) {
    return { valid: false, error: `Please enter a real ${fieldLabel.toLowerCase()}, not an email` };
  }
  
  // Check if it contains URL
  if (CONTAINS_URL_PATTERN.test(trimmed)) {
    return { valid: false, error: `${fieldLabel} cannot contain URLs` };
  }
  
  // Check if it's only numbers
  if (NUMBERS_ONLY_PATTERN.test(trimmed)) {
    return { valid: false, error: `${fieldLabel} cannot be only numbers` };
  }
  
  // Check if it's only special characters
  if (SPECIAL_CHARS_ONLY_PATTERN.test(trimmed)) {
    return { valid: false, error: `${fieldLabel} must contain letters` };
  }
  
  // Check for suspicious patterns (keyboard mashing)
  const lowerName = trimmed.toLowerCase();
  const suspiciousPatterns = ['asdf', 'qwer', 'zxcv', 'test', 'fake', 'none', 'n/a', 'na', 'null', 'undefined', 'xxx', 'aaa', 'bbb'];
  if (suspiciousPatterns.some(p => lowerName === p || (lowerName.length <= 4 && lowerName.includes(p)))) {
    return { valid: false, error: `Please enter your real ${fieldLabel.toLowerCase()}` };
  }
  
  // Check for repeated characters (e.g., "aaaa", "xxxx")
  if (/^(.)\1{2,}$/.test(trimmed)) {
    return { valid: false, error: `Please enter a valid ${fieldLabel.toLowerCase()}` };
  }
  
  return { valid: true, error: null };
};

/**
 * Validate both first and last name together
 * @param {string} firstName 
 * @param {string} lastName 
 * @returns {{ valid: boolean, errors: { firstName?: string, lastName?: string } }}
 */
export const validateFullName = (firstName, lastName) => {
  const firstResult = validateName(firstName, 'First name');
  const lastResult = validateName(lastName, 'Last name');
  
  const errors = {};
  if (!firstResult.valid) errors.firstName = firstResult.error;
  if (!lastResult.valid) errors.lastName = lastResult.error;
  
  // Additional cross-field validation
  const trimmedFirst = firstName?.trim() || '';
  const trimmedLast = lastName?.trim() || '';
  
  // Check if first and last are identical
  if (trimmedFirst && trimmedLast && trimmedFirst.toLowerCase() === trimmedLast.toLowerCase()) {
    errors.lastName = 'First and last name cannot be identical';
  }
  
  // Check if they swapped email parts into name fields
  if (trimmedFirst.includes('@') || trimmedLast.includes('@')) {
    if (trimmedFirst.includes('@')) errors.firstName = 'Please enter your first name, not email';
    if (trimmedLast.includes('@')) errors.lastName = 'Please enter your last name, not email';
  }
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Format name properly (capitalize first letter)
 * @param {string} name 
 * @returns {string}
 */
export const formatName = (name) => {
  if (!name) return '';
  const trimmed = name.trim();
  if (trimmed.length === 0) return '';
  
  // Handle hyphenated names (e.g., "Mary-Jane")
  return trimmed
    .split(/[-\s]+/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(trimmed.includes('-') ? '-' : ' ');
};

/**
 * Validate phone number
 * @param {string} phone 
 * @returns {{ valid: boolean, error: string | null }}
 */
export const validatePhone = (phone) => {
  if (!phone) {
    return { valid: false, error: 'Phone number is required' };
  }
  
  // Remove common formatting characters
  const cleaned = phone.replace(/[\s\-\(\)\.\+]/g, '').trim();
  
  // Check if it's only digits after cleaning
  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, error: 'Phone number can only contain digits' };
  }
  
  // Check minimum length (at least 10 digits)
  if (cleaned.length < 10) {
    return { valid: false, error: 'Phone number must be at least 10 digits' };
  }
  
  // Check maximum length
  if (cleaned.length > 15) {
    return { valid: false, error: 'Phone number is too long' };
  }
  
  // Check for obvious fake numbers
  const fakeNumbers = ['0000000000', '1111111111', '1234567890', '0123456789', '9999999999'];
  if (fakeNumbers.includes(cleaned)) {
    return { valid: false, error: 'Please enter a valid phone number' };
  }
  
  // Check for repeated digits (e.g., 5555555555)
  if (new Set(cleaned).size === 1) {
    return { valid: false, error: 'Please enter a valid phone number' };
  }
  
  return { valid: true, error: null };
};

/**
 * Format phone number
 * @param {string} phone 
 * @returns {string}
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  // Remove all non-digit characters except +
  return phone.replace(/[^\d+]/g, '').trim();
};

export default { validateName, validateFullName, formatName, validatePhone, formatPhone };
